import { DEV_AUTH_BYPASS, getSupabase } from "./auth";
import type {
  ConversationMessage,
  CostByCreator,
  CostByJobType,
  CostByService,
  CostTimeseriesPoint,
  CostsSummary,
  CreatorDetail,
  CreatorListItem,
  DiscoveryCandidate,
  DiscoveryRun,
  DraftPending,
  EventRow,
  MemberListItem,
  OutreachInboxItem,
  PagerLogRow,
  Paginated,
  QueueRow,
  SystemHealth,
  TriageItem,
} from "./types";

/**
 * Typed fetch wrapper for /admin/api/*.
 *
 * - Attaches Supabase Bearer token from active session.
 * - Throws ApiError on non-2xx with `.status` set so React Query can switch on it.
 */

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown> | undefined;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const BASE = "/admin/api";

async function authHeader(): Promise<HeadersInit> {
  if (DEV_AUTH_BYPASS) {
    return { "x-dev-auth": "1", "x-dev-email": "dev@ultraviolet.club" };
  }
  const supabase = getSupabase();
  if (!supabase) return {};
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) return {};
  return { Authorization: `Bearer ${session.access_token}` };
}

interface RequestOpts {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = "GET", body, query, signal } = opts;
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(await authHeader()),
  };
  if (body !== undefined) (headers as Record<string, string>)["content-type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(url.pathname + url.search, {
      method,
      headers,
      credentials: "include",
      signal,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, "network", (e as Error).message ?? "Network error");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // non-JSON — preserve text
      if (!res.ok) {
        throw new ApiError(res.status, "non_json", text.slice(0, 200));
      }
      return text as unknown as T;
    }
  }

  if (!res.ok) {
    const e = (json as { error?: { code?: string; message?: string; details?: any } } | null)
      ?.error;
    throw new ApiError(res.status, e?.code ?? "unknown", e?.message ?? res.statusText, e?.details);
  }

  return json as T;
}

/* ---------- typed endpoints ---------- */

/**
 * Many of our list endpoints return `{items: [...], next_cursor: ...}` per the
 * ADMIN_API_CONTRACT — but the methods below are typed as `T[]` for ergonomics.
 * `unwrapItems` reaches into the envelope so the component code can keep using
 * arrays directly. Pass-through if the response is already an array.
 */
async function requestArray<T>(path: string, opts?: RequestOpts): Promise<T[]> {
  const r = await request<T[] | { items: T[] }>(path, opts);
  if (Array.isArray(r)) return r;
  if (r && typeof r === "object" && Array.isArray((r as { items?: T[] }).items)) {
    return (r as { items: T[] }).items;
  }
  return [];
}

export const api = {
  /* Triage */
  triage(query?: { type?: string; since?: string; limit?: number }) {
    return requestArray<TriageItem>("/triage", { query });
  },

  /* Creators */
  creatorsList(query?: {
    status?: string;
    search?: string;
    sort?: string;
    dir?: "asc" | "desc";
    limit?: number;
    cursor?: string;
  }) {
    return request<Paginated<CreatorListItem>>("/creators", { query });
  },
  creator(creatorId: string) {
    return request<CreatorDetail>(`/creators/${encodeURIComponent(creatorId)}`);
  },
  patchCreator(creatorId: string, body: Record<string, unknown>) {
    return request<CreatorDetail>(`/creators/${encodeURIComponent(creatorId)}`, {
      method: "PATCH",
      body,
    });
  },
  pauseCreator(creatorId: string, body: { until: string | null; reason: string }) {
    return request<{ ok: true }>(`/creators/${encodeURIComponent(creatorId)}/pause`, {
      method: "POST",
      body,
    });
  },
  unpauseCreator(creatorId: string) {
    return request<{ ok: true }>(`/creators/${encodeURIComponent(creatorId)}/unpause`, {
      method: "POST",
    });
  },

  /* Members + threads */
  members(
    creatorId: string,
    query?: {
      stage?: string;
      detection?: string;
      search?: string;
      limit?: number;
      cursor?: string;
    },
  ) {
    return request<Paginated<MemberListItem>>(
      `/creators/${encodeURIComponent(creatorId)}/members`,
      { query },
    );
  },
  thread(creatorId: string, memberId: string, query?: { limit?: number; before?: string }) {
    return request<Paginated<ConversationMessage>>(
      `/conversations/${encodeURIComponent(creatorId)}/${encodeURIComponent(memberId)}/thread`,
      { query },
    );
  },

  /* Drafts */
  drafts(query?: { creator_id?: string; kind?: string; limit?: number }) {
    return requestArray<DraftPending>("/drafts/pending", { query });
  },
  approveDraft(draftId: string) {
    return request<{ ok: true; dispatched: number }>(
      `/drafts/${encodeURIComponent(draftId)}/approve`,
      { method: "POST" },
    );
  },
  editDraft(draftId: string, chunks: string[]) {
    return request<{ ok: true; dispatched: number }>(
      `/drafts/${encodeURIComponent(draftId)}/edit`,
      { method: "POST", body: { chunks } },
    );
  },
  denyDraft(draftId: string, reason: string) {
    return request<{ ok: true }>(`/drafts/${encodeURIComponent(draftId)}/deny`, {
      method: "POST",
      body: { reason },
    });
  },
  composeDraft(body: {
    creator_id: string;
    member_id: string;
    chunks: string[];
    kind?: string;
    note?: string;
  }) {
    return request<{ ok: true; draft_id: string }>("/drafts/compose", {
      method: "POST",
      body,
    });
  },

  /* System */
  systemHealth() {
    return request<SystemHealth>("/system/health");
  },
  systemQueue(query?: { status?: string; job_type?: string; limit?: number }) {
    return requestArray<QueueRow>("/system/queue", { query });
  },
  systemEvents(query?: {
    service?: string;
    event_type?: string;
    since?: string;
    limit?: number;
  }) {
    return requestArray<EventRow>("/system/events", { query });
  },
  pagerLog(query?: { limit?: number }) {
    return requestArray<PagerLogRow>("/system/pager_log", { query });
  },

  /* Costs */
  costsSummary() {
    return request<CostsSummary>("/costs/summary");
  },
  costsByCreator(query?: { days?: number }) {
    return requestArray<CostByCreator>("/costs/by_creator", { query });
  },
  costsByService(query?: { days?: number }) {
    return requestArray<CostByService>("/costs/by_service", { query });
  },
  costsByJobType(query?: { days?: number }) {
    return requestArray<CostByJobType>("/costs/by_job_type", { query });
  },
  costsTimeseries(query?: { days?: number; group_by?: "day" | "hour" }) {
    return requestArray<CostTimeseriesPoint>("/costs/timeseries", { query });
  },

  /* Recruiting */
  discoveryRuns() {
    return requestArray<DiscoveryRun>("/recruiting/discovery_runs");
  },
  discoveryCandidates() {
    return requestArray<DiscoveryCandidate>("/recruiting/candidates");
  },
  outreach() {
    return requestArray<OutreachInboxItem>("/recruiting/outreach");
  },
  kickDiscovery(rule_id: string) {
    return request<{ ok: true; run_id: string }>("/recruiting/discovery/kick", {
      method: "POST",
      body: { rule_id },
    });
  },

  /* Creator ↔ Kitt chat log */
  creatorChatLog(creatorId: string, query?: { limit?: number }) {
    return request<{ items: CreatorChatLogRow[] }>(
      `/creators/${encodeURIComponent(creatorId)}/kitt_chat`,
      { query },
    );
  },

  /* Activity feed (live event stream) */
  activityFeed(query?: { limit?: number; types?: string }) {
    return requestArray<ActivityRow>("/activity", { query });
  },

  /* UV Creator Tools (iOS fleet) — read-only views */
  fleetStatus() {
    return request<{ summary: FleetSummary; devices: FleetDevice[] }>(
      "/fleet/status",
    );
  },
  fleetQueue() {
    return request<{
      by_recruiter: Array<{
        assigned_recruiter_name: string;
        pending: number;
        in_flight: number;
        sent_last_hour: number;
        failed_last_hour: number;
      }>;
      recent_failures: Array<{ error: string; count: number }>;
    }>("/fleet/queue");
  },
  fleetReplyRate(days = 7) {
    return request<{
      days: number;
      items: Array<{
        recruiter_name: string;
        sent: number;
        replied: number;
        reply_rate_pct: number;
      }>;
    }>("/fleet/reply_rate", { query: { days } });
  },
  fleetCandidatesSummary() {
    return request<{
      items: Array<{ tier: string; total: number; unassigned: number }>;
    }>("/fleet/candidates_summary");
  },

  /* Ultradash AI chat (Phase A-F) */
  ultradashSession() {
    return request<{
      active: boolean;
      email?: string;
      organization_role?: string;
      assigned_club_ids?: string[];
      expires_at?: string;
    }>("/ultradash/session");
  },
  ultradashAuthCode(email: string) {
    return request<{ ok: true }>("/ultradash/auth/code", {
      method: "POST", body: { email },
    });
  },
  ultradashAuthVerify(email: string, code: string) {
    return request<{
      ok: true; organization_role?: string; assigned_club_ids?: string[];
    }>("/ultradash/auth/verify", { method: "POST", body: { email, code } });
  },
  ultradashDashboard() {
    return request<{
      by_creator: Array<{
        club_id: string;
        outbound_msgs_7d: number;
        ai_msgs_7d: number;
        human_msgs_7d: number;
        hybrid_msgs_7d: number;
        organic_events_7d: number;
        organic_cents_7d: number;
        ai_events_7d: number;
        ai_cents_7d: number;
        hybrid_events_7d: number;
        hybrid_cents_7d: number;
      }>;
      holdouts: Array<{ club_id: string; n_holdouts: number }>;
    }>("/ultradash/dashboard");
  },
  ultradashDrafts(query?: { creator_id?: string; status?: string; limit?: number }) {
    return request<{ items: UltradashDraft[] }>("/ultradash/drafts", { query });
  },
  ultradashDraft(id: number) {
    return request<{ draft: UltradashDraft;
      history: Array<{ direction: string; composer: string; message_text: string; occurred_at: string }>
    }>(`/ultradash/drafts/${id}`);
  },
  ultradashDraftApprove(id: number) {
    return request<{ ok: true; draft_id: number; stream_message_id?: string }>(
      `/ultradash/drafts/${id}/approve`, { method: "POST" }
    );
  },
  ultradashDraftEditSend(id: number, text: string) {
    return request<{ ok: true; draft_id: number }>(
      `/ultradash/drafts/${id}/edit-and-send`, { method: "POST", body: { text } }
    );
  },
  ultradashDraftReject(id: number, reason?: string) {
    return request<{ ok: true; draft_id: number }>(
      `/ultradash/drafts/${id}/reject`, { method: "POST", body: { reason } }
    );
  },
  ultradashDraftsBulkApprove(query?: { creator_id?: string }) {
    return request<{ sent: number; failed: Array<{ id: number; error: string }> }>(
      "/ultradash/drafts/bulk-approve", { method: "POST", query }
    );
  },
  ultradashQueueSummary() {
    return request<{ items: UltradashQueueRow[] }>("/ultradash/queue/summary");
  },
  ultradashSetCreatorMode(args: {
    creator_id: string; mode: "review_required" | "auto"; ai_chat_enabled?: boolean;
  }) {
    return request<{ ok: true }>("/ultradash/creator-approval-mode", {
      method: "POST", body: args,
    });
  },

  /* Recruiters (the team) — Bundle 9/10 ops */
  recruitersList(query?: { include_inactive?: boolean; limit?: number }) {
    return requestArray<Recruiter>("/recruiters", { query });
  },
  recruiter(recruiterId: string) {
    return request<RecruiterDetail>(`/recruiters/${encodeURIComponent(recruiterId)}`);
  },
  recruiterChatLog(recruiterId: string, query?: { limit?: number }) {
    return request<{ items: RecruiterChatLogRow[]; bb_chat_guid: string | null }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/chat_log`,
      { query },
    );
  },
  onboardRecruiter(
    recruiterId: string,
    body: { addresses: string[]; kickoff_text: string; enqueue_checkin?: boolean },
  ) {
    return request<{ ok: true; job_id: string; host_id: string }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/onboard`,
      { method: "POST", body },
    );
  },
  toggleRecruiterKillSwitch(recruiterId: string, enabled: boolean) {
    return request<{ ok: true; chat_kill_switch: boolean }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/kill_switch`,
      { method: "POST", body: { enabled } },
    );
  },
  sendRecruiterCheckinNow(recruiterId: string) {
    return request<{ ok: true; job_id: string }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/send_now`,
      { method: "POST" },
    );
  },
  updateRecruiterQuotas(
    recruiterId: string,
    body: {
      daily_dm_quota?: number;
      monthly_launch_quota?: number;
      checkin_hour?: number;
      checkin_timezone?: string;
    },
  ) {
    return request<{ ok: true }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/quotas`,
      { method: "POST", body },
    );
  },
  updateRecruiterMiddayPulse(
    recruiterId: string,
    body: { enabled?: boolean; hour?: number },
  ) {
    return request<{ ok: true; midday_pulse_enabled: boolean; midday_pulse_hour: number }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/midday_pulse`,
      { method: "POST", body },
    );
  },
  sendRecruiterMiddayPulseNow(recruiterId: string) {
    return request<{ ok: true; job_id: string }>(
      `/recruiters/${encodeURIComponent(recruiterId)}/midday_pulse_now`,
      { method: "POST" },
    );
  },
  manualLaunches(recruiterId: string, query?: { status?: string; limit?: number }) {
    return requestArray<ManualLaunch>(
      `/recruiters/${encodeURIComponent(recruiterId)}/manual_launches`,
      { query },
    );
  },
  confirmManualLaunch(launchId: string) {
    return request<{ ok: true }>(
      `/manual_launches/${encodeURIComponent(launchId)}/confirm`,
      { method: "POST" },
    );
  },
  rejectManualLaunch(launchId: string, reason: string) {
    return request<{ ok: true }>(
      `/manual_launches/${encodeURIComponent(launchId)}/reject`,
      { method: "POST", body: { reason } },
    );
  },
};

// Recruiter (team) types
export interface Recruiter {
  recruiter_id: string;
  name: string;
  recruiter_type: "app_dm" | "manual";
  phone: string | null;
  bb_group_chat_guid: string | null;
  linked_creator_username: string | null;
  daily_dm_quota: number;
  monthly_launch_quota: number | null;
  is_paid: boolean;
  streak_misses: number;
  last_checkin_at: string | null;
  checkin_hour: number;
  checkin_timezone: string;
  chat_kill_switch: boolean;
  is_active: boolean;
  midday_pulse_enabled: boolean;
  midday_pulse_hour: number;
  onboarded_at: string | null;
  created_at: string | null;
  dms_yday: number;
  dms_last_7d: number;
  mtd_launches: number;
  is_onboarded: boolean;
}

export interface RecruiterDetail extends Recruiter {
  instagram_account?: string | null;
  memory: {
    context_summary: string | null;
    last_interaction_summary: string | null;
    last_interaction_at: string | null;
    communication_style: string | null;
    preferred_name: string | null;
  } | null;
  last_checkin: {
    checkin_id: string;
    checkin_date: string | null;
    kitt_message: string;
    message_kind: string;
    metrics: Record<string, unknown> | null;
    status: string;
    sent_at: string | null;
  } | null;
  last_midday_pulse: {
    pulse_id: string;
    checkin_date: string | null;
    kitt_message: string;
    message_kind: string;
    metrics: Record<string, unknown> | null;
    status: string;
    sent_at: string | null;
  } | null;
}

export interface RecruiterChatLogRow {
  id: string;
  direction: "inbound" | "outbound";
  sender_phone: string | null;
  sender_role: string | null;
  message_text: string | null;
  was_ai_generated: boolean;
  ai_intent: string | null;
  message_timestamp: string | null;
  bb_message_guid: string | null;
}

export interface CreatorChatLogRow {
  id: string;
  direction: "inbound" | "outbound";
  message_text: string | null;
  was_ai_generated: boolean;
  ai_intent: string | null;
  message_timestamp: string | null;
}

export interface FleetDevice {
  recruiter_name: string;
  recruiter_id: string | null;
  device_id: string | null;
  app_version: string | null;
  online: boolean;
  is_sending: boolean;
  barb_monitoring: boolean;
  daily_sent: number;
  daily_limit: number;
  daily_remaining: number;
  utilization_pct: number;
  hourly_sent: number;
  hourly_limit: number;
  queued_recipients: number;
  active_campaigns: number;
  barb_replied: number;
  barb_pending: number;
  human_owned: number;
  battery: number | null;
  network: string | null;
  last_heartbeat_at: string | null;
  seconds_since_heartbeat: number | null;
}

export interface FleetSummary {
  total_devices: number;
  online_devices: number;
  offline_devices: number;
  total_sent_today: number;
  total_capacity_today: number;
  utilization_pct: number;
  barb_pending: number;
  barb_replied: number;
  stuck: string[];
}

export interface ActivityRow {
  id: string;
  service: string;
  event_type: string;
  occurred_at: string;
  cost_cents: number;
  duration_ms: number | null;
  trace_id: string | null;
  payload: Record<string, unknown>;
  preview: string;
  severity: "info" | "warn" | "urgent";
  label: string;
}

export interface ManualLaunch {
  id: string;
  club_name: string | null;
  creator_username: string | null;
  creator_phone: string | null;
  launched_on: string | null;
  status: "reported" | "confirmed" | "rejected";
  confirmed_at: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface UltradashDraft {
  id: number;
  creator_id: string;
  creator_display_name: string | null;
  club_id: string;
  channel_type: "messaging" | "clubChat";
  target_id: string;
  member_id: string | null;
  draft_text: string;
  edited_text: string | null;
  voice_clone_id: string | null;
  inbound_summary: string | null;
  model_used: string | null;
  status: "pending_review" | "approved" | "sent" | "rejected" | "expired" | "edited";
  reviewed_by: string | null;
  reviewed_at: string | null;
  sent_at: string | null;
  send_error: string | null;
  created_at: string;
}

export interface UltradashQueueRow {
  creator_label: string;
  creator_id: string;
  ultradash_approval_mode: "review_required" | "auto" | null;
  ai_chat_enabled: boolean | null;
  voice_clone_id: string | null;
  pending: number;
  sent_24h: number;
  rejected_24h: number;
}

export type Api = typeof api;
